/**
 * Static marketing & brand assets served from S3 (www.myorglab.com bucket).
 *
 * Production: REACT_APP_STATIC_ASSET_URL=https://www.myorglab.com (default)
 * Local dev: same URL so images load from S3 instead of the CRA bundle.
 */

export const STATIC_ASSET_BASE = (
  process.env.REACT_APP_STATIC_ASSET_URL || 'https://www.myorglab.com'
).replace(/\/$/, '');

/** @param {string} path - Root-relative path, e.g. `/images/logos/foo.png` */
export function assetUrl(path, local = false) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${local ? (process.env.PUBLIC_URL || '') : STATIC_ASSET_BASE}${normalized}`;
}

/**
 * Homepage brand intro assets. The live intro uses the 2D mark only.
 * Keep the GLB / poster paths so a later 3D pass can reuse them without
 * re-adding files.
 */
export const HOME_SCULPTURE = {
  model: assetUrl('/images/3d/myorglab-m-crisp.glb', true),
  poster: assetUrl('/images/3d/myorglab-m-native.poster.webp', true),
  logoSource: assetUrl('/images/logos/PNG+Logo+Files/Transparent+Logo.png'),
  mark: assetUrl('/Logo.png'),
  wordmark: assetUrl('/LogoFull.png'),
};

/** Open Graph / social preview */
export const OG_PREVIEW = assetUrl('/og-preview.png');

/** Brand marks */
export const BRAND = {
  logoTransparent: assetUrl('/images/logos/PNG+Logo+Files/Transparent+Logo.png'),
  favicon: assetUrl('/images/logos/Favicon/Favicon+Transparent.ico'),
  logoMark: assetUrl('/Logo.png'),
  logoFull: assetUrl('/LogoFull.png'),
  faviconRoot: assetUrl('/FaviconTransparent.ico'),
};

/** Partner / platform logos */
export const PARTNER_LOGOS = {
  snowflake: assetUrl('/logos/snowflake.svg'),
  powerbi: assetUrl('/logos/powerbi.svg'),
  aws: assetUrl('/logos/aws.svg'),
  poweredBySnowflake: assetUrl('/images/company_logo_slideshow/power-by.png'),
};

/** Platform engineering service illustrations */
export const PLATFORM_ENGINEERING = {
  legacyModernization: assetUrl('/images/generic_site_images/legacy_modernization_data_engineering_page.png'),
  agenticRag: assetUrl('/images/generic_site_images/agentic_rag_data_engineering_page.png'),
  crmHris: assetUrl('/images/generic_site_images/crm_hris_data_engineering_page.png'),
  dashboardCta: assetUrl('/images/generic_site_images/dashboard_3_home_page.png'),
  laptopHome: assetUrl('/images/generic_site_images/laptop_home_page.jpg'),
};

/** Interactive analytics page media */
export const INTERACTIVE_ANALYTICS = {
  requirementsGif: assetUrl('/images/generic_site_images/develop_requirements_pbi_page.gif'),
  governanceGif: assetUrl('/images/generic_site_images/data_governance_pbi_page.gif'),
  contextGapBase: assetUrl('/images/interactive-analytics/context-gap'),
  governanceLayersBase: assetUrl('/images/interactive-analytics/governance-layers'),
  governanceTriangleBase: assetUrl('/images/interactive-analytics/governance_triangle_frames_transparent'),
  commandHub: {
    hub: assetUrl('/images/interactive-analytics/command-hub/command-hub.png'),
    chat: assetUrl('/images/interactive-analytics/command-hub/chat.png'),
    report: assetUrl('/images/interactive-analytics/command-hub/report.png'),
    chart: assetUrl('/images/interactive-analytics/command-hub/chart.png'),
  },
};

/** Company / about page media */
export const COMPANY = {
  missionHandsLaptop: assetUrl('/images/generic_site_images/hands_laptop_mission_page.jpg'),
  missionProduct: assetUrl('/images/generic_site_images/dashboard_3_home_page.png'),
  missionWorkspace: assetUrl('/images/generic_site_images/laptop_home_page.jpg'),
  /** Portrait-centric stock; keep for archived people modules, not the live /company page. */
  missionTeam: assetUrl('/images/generic_site_images/muslim_black_people_mission_page.jpg'),
  monroeIcon: assetUrl('/images/generic_site_images/monroe_acquisition_company_page.ico'),
  sofIconOriginal: assetUrl('/images/generic_site_images/sof_icon_company_page.png'),
  sofIcon: assetUrl('/images/generic_site_images/sof_icon_company_page_tight.png', true),
  distrocode: assetUrl('/images/generic_site_images/distrocode_company_page.jpg'),
  founder: assetUrl('/images/management_photos/michael_fawehinmi.png'),
  leadership: {
    michael: assetUrl('/images/management_photos/michael_fawehinmi.png'),
    aparna: assetUrl('/images/management_photos/aparna_ramani.jpg'),
    rajesh: assetUrl('/images/management_photos/rajesh_patel.jpg'),
    ananya: assetUrl('/images/management_photos/ananya_iyer.jpg'),
    prateek: assetUrl('/images/management_photos/prateek_shah.jpg'),
    ahmed: assetUrl('/images/management_photos/ahmed_bennalouche.jpg'),
  },
};

/** Semantic HR tour reference screenshots */
export const SEMANTIC_HR_TOUR_REF = {
  termination: assetUrl('/images/semantic-hr/media/tour-ref/pbi-termination.png'),
  headcount: assetUrl('/images/semantic-hr/media/tour-ref/pbi-headcount.png'),
  hires: assetUrl('/images/semantic-hr/media/tour-ref/pbi-hires.png'),
};
